import React, { useState } from "react";
import {
  Box,
  makeStyles,
  Typography,
  Grid,
  Collapse,
  CircularProgress,
  colors,
} from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";
import { FormikHelpers, useFormik } from "formik";

import { PageTitle } from "../../../../../components/page-title/PageTitle";
import { Status } from "../../../components/Status";
import ExpandedContent from "../../../components/ExpandedContent";
import SettingsItem from "../../../components/SettingsItem";
import { DefaultButton, OutlineButton } from "../../../components/Buttons";
import { InputField } from "../../../../../components/input-field/InputField";
import { PIN_MAX_SIZE } from "../../../../../settings/constants";
import ICIPAdminService from "../../../../../services/ICIPAdminService";
import User from "../../../../../models/User";

interface SettingsOverviewProps {
  user: User;
}

const useStyles = makeStyles(
  (theme) => ({
    root: {},
    subMain: {
      padding: "32px 32px 10px 32px",
    },
    actions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
    },

    actionItem: {
      marginLeft: 20,
    },
  }),
  { name: "personal-setting-overview-tab-styles" }
);

export default function SettingsOverview(props: SettingsOverviewProps) {
  const classes = useStyles(props);
  const intl = useIntl();
  // State
  const [user, setUser] = useState(props.user);

  return (
    <>
      <PageTitle
        withAppTitle={false}
        title={`${intl.formatMessage({
          id: "sharedScreen.admin",
        })} - ${intl.formatMessage({
          id: "sharedScreen.personalAccountSettings",
        })}`}
      />
      <Box component="div" className={classes.root}>
        <Box component="div">
          <Box component="div">
            <Typography variant="h4" component="h4" className={classes.subMain}>
              <FormattedMessage id="sharedScreen.settings" />
            </Typography>

            <Box component="div" className={classes.actions}>
              <Box className={classes.actionItem}>
                <Status
                  withBorder
                  withUppercase
                  size={11}
                  status={user.accountStatus()}
                />
              </Box>
            </Box>
          </Box>
          <ExpandedContent
            title={intl.formatMessage({ id: "sharedScreen.security" })}
          >
            <SettingsItem
              title={intl.formatMessage({ id: "sharedScreen.resetPin" })}
              subTitle={intl.formatMessage({
                id: "sharedScreen.resetPinNotice",
              })}
            >
              <ResetPin user={user} />
            </SettingsItem>
          </ExpandedContent>
        </Box>
      </Box>
    </>
  );
}

interface ResetPinProps {
  user: User;
}

const useResetPinStyles = makeStyles(
  (theme) => ({
    root: {
      marginTop: theme.spacing(2),
    },

    actions: {},

    resetPanel: {
      marginBottom: theme.spacing(2),
    },

    loader: {
      marginTop: 8,
    },

    error: {
      fontSize: 13,
      color: colors.red[700],
      textAlign: "left",
    },
  }),
  { name: "setting-reset-pin-styles" }
);

export function ResetPin(props: ResetPinProps) {
  const classes = useResetPinStyles(props);
  const intl = useIntl();
  // State
  const [openFormPanel, setOpenFormPanel] = useState(false);
  const [user, setUser] = useState(props.user);

  // Events call

  const openFormWrapperAction = (status: boolean) => {
    setOpenFormPanel(status);
  };

  const actionState = () => {
    return (
      formik.values.pin_code.length === PIN_MAX_SIZE && !formik.isSubmitting
    );
  };

  const pinChange = (event: any) => {
    let pin: string = event.currentTarget.value;

    if ((pin.length !== 0 && !Number(pin)) || pin.length === PIN_MAX_SIZE + 1)
      return;
    formik.setFieldValue("pin_code", pin);
  };

  // Handle formik

  const initFormValues = () => {
    return {
      pin_code: "",
    };
  };

  const formValidation = () => {
    return {
      pin_code: Yup.string().required(
        intl.formatMessage({ id: "error.resetPin" }, { digit: PIN_MAX_SIZE })
      ),
    };
  };

  const formik = useFormik({
    initialValues: initFormValues(),
    validationSchema: Yup.object().shape({
      ...formValidation(),
    }),
    onSubmit: async (
      values: any,
      { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        const response = await ICIPAdminService.resetUserPin(
          user.id,
          formik.values.pin_code
        );
        setSubmitting(false);

        if (response.isSuccess) {
          switch (response.data.code) {
            case 100: // Ok
              user.setBlockedAt(null);
              openFormWrapperAction(false);
              break;
            case 150:
              // Validation error
              setErrors(JSON.parse(JSON.stringify(response.data.data)));
              break;
            default:
              setErrors({ error: response.data.message });
              break;
          }
        } else {
          setErrors({
            unknown: intl.formatMessage({ id: "error.unknownError" }),
          });
        }
      } catch (error) {
        setErrors({
          unknown: intl.formatMessage({ id: "error.unknownError" }),
        });
      }
    },
  });

  // Render

  const genericError = () => {
    if (formik.errors.unknown) {
      return formik.errors.unknown as string;
    }

    if (formik.errors.pin_code) {
      return formik.errors.pin_code as string;
    }

    if (formik.errors.user_id) {
      return formik.errors.user_id as string;
    }

    if (formik.errors.error) {
      return formik.errors.error as string;
    }

    return "";
  };

  return (
    <Box className={classes.root}>
      <Collapse in={!openFormPanel} timeout="auto" unmountOnExit>
        <Box className={classes.actions}>
          <DefaultButton
            onClick={(_) => openFormWrapperAction(true)}
            disabled={!user.isLocked()}
          >
            <FormattedMessage id="sharedScreen.resetPinCode" />
          </DefaultButton>
        </Box>
      </Collapse>

      <Collapse in={openFormPanel} timeout="auto" unmountOnExit>
        <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Box className={classes.resetPanel}>
            <Grid container>
              <Grid item xs={12} sm={5}>
                <Box component="div">
                  <InputField
                    fullWidth
                    id="pin_code"
                    name="pin_code"
                    label=""
                    placeholder={intl.formatMessage(
                      { id: "sharedScreen.enterNDigitPinCode" },
                      { digit: PIN_MAX_SIZE }
                    )}
                    type="password"
                    error={
                      !Boolean(formik.touched.pin_code) &&
                      Boolean(formik.errors.pin_code)
                    }
                    disabled={formik.isSubmitting}
                    value={formik.values.pin_code}
                    helperText={
                      formik.touched.pin_code && formik.errors.pin_code
                    }
                    onChange={pinChange}
                  />
                </Box>
                <Box mt={1} component="div" className={classes.error}>
                  {genericError()}
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Box
            className={classes.actions}
            display="flex"
            alignItems="center"
            justifyContent="flex-start"
          >
            <DefaultButton type="submit" disabled={!actionState()}>
              <FormattedMessage id="sharedScreen.resetPinCode" />
            </DefaultButton>
            <Box component="div" ml={2}>
              {formik.isSubmitting ? (
                <Box display="inline-block" className={classes.loader}>
                  <CircularProgress color="secondary" size={20} thickness={4} />
                </Box>
              ) : (
                <OutlineButton onClick={(_) => openFormWrapperAction(false)}>
                  <FormattedMessage id="sharedScreen.cancel" />
                </OutlineButton>
              )}
            </Box>
          </Box>
        </form>
      </Collapse>
    </Box>
  );
}
